// The shared ruler: the measurer's ends and shape reach everyone else through
// MapChannel#ruler, and each board draws the others' rulers until the measurer
// puts the ruler away or stops being heard from.

import { knownShape, type RulerShape } from "./areas";
import { type Fog, hiddenAt } from "./fog";

export type RulerPoints = [number, number, number, number];
// What the ends mean: a plain line, or an area with a cone's end width in the grid's unit.
export type RulerKind = { shape: RulerShape; width: number | null };
const PLAIN: RulerKind = { shape: "ruler", width: null };
type Timer = ReturnType<typeof globalThis.setTimeout>;
type Latest = { points: RulerPoints; kind: RulerKind };

export const RULER_SEND_MS = 100;
// A ruler held still is sent again this often, inside the others' timeout.
export const RULER_KEEP_MS = 1000;
export const RULER_TIMEOUT_MS = 2000;

// The measurer's side: the first position at once, then at most one every
// RULER_SEND_MS, the latest included, held up until one null once the ruler is put away.
export function createRulerFeed({ send }: { send: (points: RulerPoints | null, kind: RulerKind) => void }) {
	let sent: Latest | null = null;
	let latest: Latest | null = null;
	let last = Number.NEGATIVE_INFINITY;
	let timer: Timer | null = null;

	function schedule(ms: number) {
		clearTimeout(timer);
		timer = setTimeout(flush, ms);
	}

	function flush() {
		timer = null;
		if (!latest) return;
		sent = latest;
		last = Date.now();
		send(sent.points, sent.kind);
		schedule(RULER_KEEP_MS);
	}

	return {
		update(points: RulerPoints, kind: RulerKind = PLAIN) {
			latest = { points, kind };
			if (sent && points.every((value, index) => value === sent?.points[index]) && kind.shape === sent.kind.shape && kind.width === sent.kind.width) return;
			const wait = last + RULER_SEND_MS - Date.now();
			if (wait <= 0) flush();
			else schedule(wait);
		},
		end() {
			clearTimeout(timer);
			timer = null;
			latest = null;
			if (!sent) return;
			sent = null;
			send(null, PLAIN);
		},
	};
}

// Everyone else's rulers, one per key, each hidden on its release or after
// RULER_TIMEOUT_MS without news.
export function createRemoteRulers({
	show,
	hide,
}: {
	show: (key: string, points: RulerPoints, color: string | null, kind: RulerKind) => void;
	hide: (key: string) => void;
}) {
	const timers = new Map<string, Timer>();

	function drop(key: string) {
		clearTimeout(timers.get(key));
		timers.delete(key);
		hide(key);
	}

	return {
		received({ key, points, color, shape, width }: { key: string; points: RulerPoints | null; color: string | null; shape?: unknown; width?: unknown }) {
			if (!points) return drop(key);
			show(key, points, color, { shape: knownShape(shape), width: typeof width === "number" && Number.isFinite(width) && width > 0 ? width : null });
			clearTimeout(timers.get(key));
			timers.set(
				key,
				setTimeout(() => drop(key), RULER_TIMEOUT_MS),
			);
		},
		clear() {
			for (const key of [...timers.keys()]) drop(key);
		},
	};
}

// The master's ruler is not shared while an end lies in the fog, the cells
// players see fogged.
export function endsInFog(points: RulerPoints, math: Parameters<typeof hiddenAt>[0], scene: Parameters<typeof hiddenAt>[1], fog: Fog | null | undefined) {
	const [x1, y1, x2, y2] = points;
	return hiddenAt(math, scene, fog, x1, y1) || hiddenAt(math, scene, fog, x2, y2);
}
